import React from "react"
import { connect } from "react-redux"

import { patchColumnData, editColumnData } from "app/static/frontend/sheets/sheet-action-creators"

import { UserSelect, ContactSelect } from "app/static/frontend/selects/containers/PieSelect"

import { getDisplayName } from "shared/imports/sharedHelperFunctions"
import { USER_RECIPIENT_KEY, SUPPORTER_RECIPIENT_KEY } from "shared/imports/sharedConstants"
import { CellWrapper } from "app/static/frontend/sheets/components/cells/MantineCells/CellWrapper"
import { extractAssignmentCellNames } from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"

const get_select_values = (multi, children, recipientKey) => {
    const keyId = `${recipientKey}__id`
    if (multi) {
        return children?.map(
            (assignment) =>
                // need to return undefined because default is null which is
                // an object (causes problems later on when the initial value)
                // is passed into the PieSelect
                assignment?.[keyId] || assignment?.id || undefined,
        )
    } else {
        return children?.[0]?.[keyId] || children?.[0]?.id || undefined
    }
}

// need to pass in the recipientFirstName / LastName key because they're different
// for users and supporteres
const makeAssignmentCell =
    (recipientKey, recipientFirstNameKey, recipientLastNameKey, multi = true, isContact = false) =>
    ({
        children,
        patchColumnData,
        editColumnData,
        sheetId,
        rowId,
        columnId,
        editing,
        menuShouldScrollIntoView,
        ...props
    }) => {
        const Component: React.ComponentType<any> = isContact ? ContactSelect : UserSelect
        Component.displayName = getDisplayName(isContact ? ContactSelect : UserSelect)
        const AssignmentCell = (
            <CellWrapper truncate={false} {...props}>
                {
                    // if we're currently editing the contents of this cell
                    editing ? (
                        <Component
                            // populated with the set of assigned users
                            value={get_select_values(multi, children, recipientKey)}
                            autoload={false || editing}
                            onChange={(selections) => {
                                if (!multi) {
                                    if (!Array.isArray(selections)) {
                                        selections = [selections]
                                    }
                                }

                                // add it to the store, making it look like an assignment,
                                // and send it to the backend as just an array of ids
                                // to create assignments.
                                patchColumnData({
                                    id: sheetId,
                                    columnId,
                                    rowId,
                                    newValue: selections,
                                    toStoreValue: selections.map((selection) => ({
                                        [`${recipientKey}__id`]: selection.id,
                                        [`${recipientKey}__${recipientFirstNameKey}`]: selection[recipientFirstNameKey],
                                        [`${recipientKey}__${recipientLastNameKey}`]: selection[recipientLastNameKey],
                                    })),
                                    row: props.row,
                                })
                            }}
                            menuShouldScrollIntoView={menuShouldScrollIntoView}
                            multi={multi}
                        />
                    ) : (
                        children &&
                        extractAssignmentCellNames(children, recipientKey, recipientFirstNameKey, recipientLastNameKey)
                    )
                }
            </CellWrapper>
        )
        return AssignmentCell
    }

const connectAssignment = (AssignmentCell) => connect(null, { patchColumnData, editColumnData })(AssignmentCell)

export const UserAssignmentCell = connectAssignment(makeAssignmentCell(USER_RECIPIENT_KEY, "first_name", "last_name"))

export const UserAssignmentSingleCell = connectAssignment(
    makeAssignmentCell(USER_RECIPIENT_KEY, "first_name", "last_name", false),
)

export const ContactAssignmentCell = connectAssignment(
    makeAssignmentCell(SUPPORTER_RECIPIENT_KEY, "firstname", "lastname", true, true),
)
